<template>
  <div class="max-w-6xl mx-auto space-y-8">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">SDK</h1>

    <div class="text-sm p-2 px-4 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center gap-2">
      <span>Project ID: </span>
      <input v-model="projectId" class="flex-1 p-2 py-1 border border-gray-300 rounded-md text-sm h-10" />
    </div>

    <div class="text-sm p-2 px-4 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center gap-2">
      <span>Project Signer Address: </span>
      <input v-model="projectSignerAddress" class="flex-1 p-2 py-1 border border-gray-300 rounded-md text-sm h-10" />
    </div>

    <div class="flex gap-2 items-center flex-wrap">
      <caas-passkey-button
        size="small"
        v-if="walletStore"
        :text="`Manage CAAS Passkeys`"
        :walletProvider="walletCore"
        :address="address"
        :client="caasStore.client"
        :projectId="projectId"
        :projectSignerAddress="projectSignerAddress"
        @passkey-create="handlePasskeyCreate"
        @passkey-create-first="handlePasskeyCreateFirst"
        @error="handleError"
        @dialog-open="handleDialogOpen"
        @dialog-close="handleDialogClose"
      />
    </div>

    <caas-card cardTitle="Transfer">
      <div class="space-y-2">
        <div class="break-all">
          {{ projectId }}
        </div>
        <caas-button @click="verifyHandler">Verify</caas-button>
      </div>
    </caas-card>

    <caas-card cardTitle="Passkey Is User Registered">
      <caas-card-content class="space-y-2">
        <div class="flex items-center gap-2">
          <input
            class="flex-1 p-2 py-1 border border-gray-300 rounded-md text-sm h-10"
            type="text"
            v-model="address"
            placeholder="Enter address"
          />
          <caas-button @click="isUserRegisteredQuery.refetch()" :disabled="isUserRegisteredQuery.isFetching.value"
            >Check</caas-button
          >
        </div>

        <div class="flex flex-col gap-2 text-sm">
          <div v-if="isUserRegisteredQuery.isFetching.value">Loading...</div>
          <div v-if="isUserRegisteredQuery.isError.value && !isUserRegisteredQuery.isFetching.value">
            Error: {{ isUserRegisteredQuery.error }}
          </div>
          <div v-if="isUserRegisteredQuery.isSuccess.value && !isUserRegisteredQuery.isFetching.value">
            Is User Registered:
            <span class="font-bold" :class="isUserRegisteredQuery.data.value ? 'text-green-500' : 'text-red-500'">
              {{ isUserRegisteredQuery.data.value }}</span
            >
          </div>
        </div>
      </caas-card-content>
    </caas-card>

    <caas-card cardTitle="Passkey List of Address">
      <caas-card-content class="space-y-2">
        <div class="flex items-center gap-2">
          <input
            class="flex-1 p-2 py-1 border border-gray-300 rounded-md text-sm h-10"
            type="text"
            v-model="address"
            placeholder="Enter address"
          />
          <caas-button
            @click="getPasskeysByAddressQuery.refetch()"
            :disabled="getPasskeysByAddressQuery.isFetching.value"
            >Check</caas-button
          >
        </div>

        <div class="flex flex-col gap-2 text-sm">
          <div v-if="getPasskeysByAddressQuery.isFetching.value">Loading...</div>
          <div v-if="getPasskeysByAddressQuery.isError.value && !getPasskeysByAddressQuery.isFetching.value">
            Error: {{ getPasskeysByAddressQuery.error }}
          </div>
        </div>

        <div v-if="getPasskeysByAddressQuery.isSuccess.value && !getPasskeysByAddressQuery.isFetching.value">
          <div v-for="address in getPasskeysByAddressQuery.data.value" :key="address">
            {{ address }}
          </div>
        </div>
      </caas-card-content>
    </caas-card>
  </div>
</template>

<script lang="ts" setup>
  import { useWalletStore } from '@/stores/wallet';
  import { ref, watch } from 'vue';
  import { useCaasStore } from '@/stores/caas';
  import { useQuery } from '@tanstack/vue-query';
  import { walletCore } from '@/config/wallet-core';
  import { PasskeyButtonEventDetail, PasskeyButtonErrorDetail } from '@caas-xyz/ui';
  import { MultiAgentTransaction } from '@aptos-labs/ts-sdk';

  const walletStore = useWalletStore();
  const address = ref('');
  const caasStore = useCaasStore();

  const projectId = ref<string>('0x9bb469e57da81071c7961259445b87f85434da79fcacf06b2bc670556ce94924::witness::Witness');
  const projectSignerAddress = ref<string>('0bb3972d52aed390718420b80ad213ee73e2d63177d85da06ba99a925b74bb52');

  const isUserRegisteredQuery = useQuery({
    queryKey: ['isUserRegistered', address.value],
    queryFn: async () => {
      try {
        const result = await caasStore.client.Passkey.isAddressRegistered(projectId.value, address.value);
        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: false,
  });

  const getPasskeysByAddressQuery = useQuery({
    queryKey: ['getPasskeysByAddress', address.value],
    queryFn: async () => {
      try {
        console.log('getPasskeysByAddress', projectId.value, address.value);
        const result = await caasStore.client.Passkey.getPasskeysByAddress(projectId.value, address.value);
        return result;
      } catch (error) {
        throw error;
      }
    },
    enabled: false,
  });

  const verifyHandler = async () => {
    try {
      const passkeys = await caasStore.client.Passkey.getPasskeysByAddress(projectId.value, address.value);

      const result = await caasStore.client.Passkey.signAndSubmitTransactionWithPasskey({
        payload: {
          function: '0x9bb469e57da81071c7961259445b87f85434da79fcacf06b2bc670556ce94924::passkey::passkey_verify',
          typeArguments: [projectId.value],
          functionArguments: [],
        },
        passkey: {
          publicKey: passkeys[0].publicKey,
          credentialId: passkeys[0].credentialId,
        },
        address: address.value,
        projectSignerAddress: projectSignerAddress.value,
        senderAuthenticatorSign: async (transaction: MultiAgentTransaction) => {
          const { authenticator } = await walletCore.signTransaction({ transactionOrPayload: transaction });
          return authenticator;
        },
      });

      await caasStore.client.AptosClient.waitForTransaction({
        transactionHash: result?.hash || '',
      });

      console.log('result', result.hash);
      alert('Transaction submitted successfully: ' + result?.hash);
    } catch (error) {
      console.log('error', error);
      throw error;
    }
  };

  const handlePasskeyCreate = (event: CustomEvent<PasskeyButtonEventDetail>) => {
    console.log('handlePasskeyCreate', event);
  };

  const handlePasskeyCreateFirst = (event: CustomEvent<PasskeyButtonEventDetail>) => {
    console.log('handlePasskeyCreateFirst', event);
  };

  const handleError = (event: CustomEvent<PasskeyButtonErrorDetail>) => {
    console.log('handleError', event);
    alert(event.detail.message);
  };

  const handleDialogOpen = () => {
    console.log('handleDialogOpen');
  };

  const handleDialogClose = () => {
    console.log('handleDialogClose');
  };

  watch(
    () => walletStore.currentAccount?.address,
    () => {
      address.value = walletStore.currentAccount?.address?.toStringLong() || '';
    },
    {
      immediate: true,
    }
  );
</script>

<style lang="less" scoped></style>
